import * as React from 'react';
import { createContext, useContext, useEffect, useState } from 'react';

import { useQuery } from '@apollo/client';
import {
  EnterpriseSetting_Key as EnterpriseSettingKey,
  EnterpriseSetting_ManagedLevelScopeType as ManagedLevelScopeType,
} from '__generated__/graphql-types';

import { useThirdPartyOrganization } from 'bundles/enterprise-admin/ThirdPartyOrganizationProvider';

import GetEnterpriseAutoEnrollmentStatus from '../query/GetEnterpriseAutoEnrollmentStatus.graphql';
import type {
  GetEnterpriseAutoEnrollmentStatusQuery,
  GetEnterpriseAutoEnrollmentStatusQueryVariables,
} from '../query/__generated__/GetEnterpriseAutoEnrollmentStatus';

export type EnrollmentError = {
  queryError: boolean;
  mutationError: boolean;
};

type EnrollmentContextType = {
  enrollmentEnabled: boolean;
  error: EnrollmentError;
  loading: boolean;
  setEnrollmentEnabled: (enrollmentEnabled: boolean) => void;
  setError: (error: EnrollmentError) => void;
  showNotification: boolean;
  setShowNotification: (showNotification: boolean) => void;
};

const EnrollmentContext = createContext<EnrollmentContextType>({
  enrollmentEnabled: false,
  error: { queryError: false, mutationError: false },
  loading: false,
  setEnrollmentEnabled: () => {},
  setError: () => {},
  showNotification: false,
  setShowNotification: () => {},
});
type Props = {
  children: React.ReactNode;
};

const EnrollmentContextProvider = ({ children }: Props) => {
  const thirdPartyOrganizationId = useThirdPartyOrganization();
  const [enrollmentEnabled, setEnrollmentEnabled] = useState<boolean>(false);
  const [error, setError] = useState<EnrollmentError>({ queryError: false, mutationError: false });
  const [showNotification, setShowNotification] = useState<boolean>(false);

  const {
    data,
    loading,
    error: queryError,
  } = useQuery<GetEnterpriseAutoEnrollmentStatusQuery, GetEnterpriseAutoEnrollmentStatusQueryVariables>(
    GetEnterpriseAutoEnrollmentStatus,
    {
      variables: {
        managedLevelScopeType: ManagedLevelScopeType.ThirdPartyOrganization,
        managedLevelScopeValue: thirdPartyOrganizationId.id,
        enterpriseSettingKey: EnterpriseSettingKey.EnterpriseSettingKeyLmsLxpAutoEnrollmentEnabled,
      },
      errorPolicy: 'all',
      context: { clientName: 'gatewayGql' },
    }
  );

  useEffect(() => {
    if (data) {
      let enrollmentEnabled = false;
      try {
        const parsedData = JSON.parse(data?.EnterpriseSetting?.findByScopeAndKey?.data);
        enrollmentEnabled = parsedData[EnterpriseSettingKey.EnterpriseSettingKeyLmsLxpAutoEnrollmentEnabled];
      } catch {
        setShowNotification(true);
        setError((errors: EnrollmentError) => ({ ...errors, queryError: true }));
        enrollmentEnabled = false;
      }
      setEnrollmentEnabled(enrollmentEnabled);
    }
    if (queryError) {
      setShowNotification(true);
      setError((errors: EnrollmentError) => ({ ...errors, queryError: true }));
    }
  }, [data, queryError]);

  return (
    <EnrollmentContext.Provider
      value={{
        enrollmentEnabled,
        error,
        loading,
        setEnrollmentEnabled,
        setError,
        showNotification,
        setShowNotification,
      }}
    >
      {children}
    </EnrollmentContext.Provider>
  );
};

export const useEnrollment = () => {
  const context = useContext(EnrollmentContext);
  if (context === undefined) {
    throw new Error('useEnrollment must be used within an EnrollmentContextProvider');
  }
  return context;
};

export default EnrollmentContextProvider;
